import Status from 'http-status-codes'
import _ from 'lodash'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import authentication from '@/authentication'
import { tokenLogin, getAuthorizedRegions, regionLogin } from '@/api/auth/request'
import { checkStatus } from '@/api/users/request'
import { aclHelper } from '@/utils/aclHelper'
import { globalPwdLogin, getUserByAdId, globalRegionLogin } from '@/api/global/request'
import CmsModulesStatusMixin from '@/modules/CmsModulesStatusMixin'
import navigation from '@/utils/navigation'
import router from '@/router'
import types from '@/store/mutation-types'
import { getCurUserInfoByAdId } from '@/api/users/users.request'
import { dashboardRouterNames, regionNames as REGION_NAMES } from '@/utils/consts'
import { getDashboard } from '@/utils/dashboardUtils'
import { resolveLoginNextPage } from './loginRedirect'

export default {
  mixins: [CmsModulesStatusMixin],
  computed: {
    ...mapGetters('regions', ['enableWorkPermitsFeature', 'enableDirectoryFeature', 'enableTenantProjectsFeature', 'activeRegions', 'allRegions', 'isCrossRegionLogin']),
    ...mapGetters('user', ['currentUser', 'currentGlobalUser']),
    ...mapGetters('settings', ['jwt']),
    currentDomainActiveRegions () {
      return this.activeRegions.filter(region => !this.isOtherDomain(region))
    },
    matchedActiveRegion () {
      const defaultRegion = _.get(this.currentGlobalUser, 'defaultRegion')
      if (defaultRegion && this.isActiveRegion(defaultRegion.name) &&
        !this.isOtherDomain(defaultRegion)
      ) {
        return defaultRegion
      }

      if (this.currentDomainActiveRegions.length >= 1) {
        return this.currentDomainActiveRegions.find(region => !this.isGlobalRegion(region)) ||
        this.currentDomainActiveRegions[0]
      }

      if (this.activeRegions.length >= 1) {
        return this.activeRegions.find(region => !this.isGlobalRegion(region)) ||
        this.activeRegions[0]
      }
      return []
    },
  },
  methods: {
    ...mapMutations('regions', {
      setActiveRegionList: types.ACTIVE_REGIONS,
    }),
    ...mapMutations('settings', {
      setJwt: types.JSON_WEB_TOKEN,
    }),
    ...mapActions('access', ['loadPerms']),
    ...mapActions('user', ['postLoginAction', 'updateCurrentGlobalUser', 'logout']),
    ...mapActions('regions', ['updateCurrentRegion', 'initRegionData', 'setIsCrossRegionLogin']),
    ...mapActions('language', ['switchLang']),
    hasCmsAccess () {
      return this.enableCms && (aclHelper.hasProjectsAccess() || aclHelper.hasTendersAccess() || aclHelper.hasTenderRepAccess())
    },
    hasWpAccess () {
      return this.enableWorkPermitsFeature && aclHelper.showWorkPermitDashboard()
    },
    hasTmsAccess () {
      return (this.enableTenantProjectsFeature || this.enableDirectoryFeature) && aclHelper.showTmsDashboard()
    },
    getDashboard (dashboardValue) {
      const accessFnMap = {
        hasCmsAccess: this.hasCmsAccess,
        hasWpAccess: this.hasWpAccess,
        hasTmsAccess: this.hasTmsAccess,
      }
      return getDashboard(dashboardValue, accessFnMap)
    },
    async toNextPage (defaultDashboard, finishCallback = () => {}) {
      const path = router.currentRoute.params.redirect
      const name = this.getDashboard(defaultDashboard)
      navigation.setLastDashboardTab(name)
      if (path) {
        delete router.currentRoute.params.redirect
      }
      const nextPage = resolveLoginNextPage({
        dashboardName: name,
        redirectPath: path,
        resolveRoute: router.resolve.bind(router),
      })
      await router.push(nextPage, finishCallback, finishCallback)
    },
    async redirectToDashboard (user, finishCallback = () => {}) {
      await this.toNextPage(_.get(user, 'user.dashboard', {}), finishCallback)
    },
    async setUserActions (user) {
      await this.postLoginAction({ user: user.user })
      await this.loadPerms({ perms: user.acl })
    },
    async getGlobalUserInfo () {
      try {
        let adId = this.currentUser.AD_id || authentication.getUserProfile().oid
        const userProfile = await getUserByAdId(adId)
        return userProfile
      } catch (error) {
        console.error(error)
        this.logout()
      }
    },
    async setAuthorizedActiveRegionList () {
      // For cross-region login, we already have the JWT set, so we don't need ADAL token
      const needToken = !this.isCrossRegionLogin
      const response = await getAuthorizedRegions(this.currentGlobalUser.AD_id, needToken)
      const activeRegions = _.get(response, 'data.data', [])
      const authorizedRegions = this.currentGlobalUser.regions.filter(region => {
        const activeAndAuthorized = activeRegions.some(activeRegion => activeRegion.enabled && region.name.toLowerCase() === activeRegion.name)
        return region.name === REGION_NAMES.global || activeAndAuthorized
      })
      if (_.isEmpty(authorizedRegions)) throw new Error('There is not validation region for login user, Please check this user in Global system')
      this.setActiveRegionList(authorizedRegions)
    },
    isActiveRegion (regionName) {
      return this.activeRegions.some(item => item.name === regionName)
    },
    setActiveCurrentRegion (regionName) {
      if (this.isActiveRegion(regionName)) {
        const nonGlobalRegion = this.activeRegions.find(region => region.name === regionName)
        regionName = nonGlobalRegion.name
        this.updateCurrentRegion(regionName)
      }
    },
    async initGlobalUserAndActiveRegionInfos (globalUserInfo) {
      await this.updateCurrentGlobalUser(globalUserInfo)
      await this.setAuthorizedActiveRegionList()
    },
    getTargetRegion () {
      const userDefaultRegion = _.get(this.matchedActiveRegion, 'name', null)
      const regionName = _.get(this.$route.query, 'region', userDefaultRegion)
      return this.allRegions[regionName]
    },
    async tokenLogin () {
      try {
        await tokenLogin()
      } catch (err) {
        if (err.response.status === Status.GONE) {
          this.logout()
        }
        throw err
      }
    },
    async regionLogin (region, token) {
      await this.initRegionData()
      const res = await regionLogin({ region, token })
      const {
        data: { data: user },
        headers,
      } = res

      if (!user || !user.id) {
        throw new Error('User data is missing from region-login response')
      }

      this.setJwt(headers.authorization)
      // Mark as cross-region login to skip local tokenLogin and redirect check
      this.setIsCrossRegionLogin(true)
      this.crossRegionTargetRegion = region
      await this.afterLoginActions(user)
    },
    async pwdLogin (username, password) {
      await this.initRegionData()
      const globalResponse = await globalPwdLogin({ username, password })
      const globalUserInfo = globalResponse.data
      this.setJwt(globalResponse.headers.authorization)
      this.setIsCrossRegionLogin(false)
      await this.afterLoginActions(globalUserInfo)
    },
    async hklUserAdLogin () {
      await this.initRegionData()
      if (!authentication.isAuthenticated()) {
        await authentication.signIn()
      } else {
        const globalUserInfo = await this.getGlobalUserInfo()
        this.setIsCrossRegionLogin(false)
        await this.afterLoginActions(globalUserInfo)
      }
    },
    async refreshUserStore (adId) {
      // For cross-region login, we already have the JWT set, so we don't need ADAL token
      const needToken = !this.isCrossRegionLogin
      const user = await getCurUserInfoByAdId(needToken)
      await this.setUserActions(user)
      return user
    },
    async checkIfRedirectToOtherSite (region, onStayPage, onLeavePage) {
      const targetRegion = region || this.getTargetRegion()
      if (this.isGlobalRegion(targetRegion)) {
        this.redirectToOtherSite(targetRegion)
        typeof onLeavePage === 'function' && onLeavePage()
      } else if (this.isOtherDomain(targetRegion)) {
        await checkStatus(true)
        const { data: region } = await globalRegionLogin(
          { region: targetRegion.name },
          { headers: { needToken: true } },
        )
        this.redirectToOtherSite(region)
        typeof onLeavePage === 'function' && onLeavePage()
      } else {
        this.setActiveCurrentRegion(targetRegion.name)
        typeof onStayPage === 'function' && (await onStayPage())
      }
    },
    async afterLoginActions (globalUser) {
      try {
        await this.initGlobalUserAndActiveRegionInfos(globalUser)

        // For cross-region login, skip checkIfRedirectToOtherSite and tokenLogin
        if (this.isCrossRegionLogin) {
          // Set current region for cross-region login
          await this.updateCurrentRegion(this.crossRegionTargetRegion)
          const user = await this.refreshUserStore(this.currentGlobalUser.AD_id)
          await this.switchLang()
          await this.redirectToDashboard(user)
        } else {
          // Normal login flow
          await this.checkIfRedirectToOtherSite(undefined, async () => {
            await this.tokenLogin()
            const user = await this.refreshUserStore(this.currentGlobalUser.AD_id)
            await this.switchLang()
            await this.redirectToDashboard(user)
          }, () => {
            this.logout({ finishCallback: this.logoutCompleteCallBack })
          })
        }
      } catch (err) {
        this.logout({ finishCallback: this.logoutCompleteCallBack })
        throw err
      }
    },
    isGlobalRegion (region) {
      return region.name === REGION_NAMES.global
    },
    isOtherDomain (region) {
      try {
        const targetDomain = (new URL(region.domain)).origin
        return targetDomain !== window.location.origin
      } catch (error) {
        console.error(`Domain column is a invalid URL: ${region}. Please check the Global system`)
        throw error
      }
    },
    redirectToOtherSite (region) {
      window.location.replace(region.domain)
    },
    logoutCompleteCallBack () {
      const cb = () => location.reload()
      this.$router.push({ name: dashboardRouterNames.login }, cb, cb)
    },
  },
}
